import { useEffect, useRef, useState } from "react";
import { Formik } from "formik";
import { Helmet } from "react-helmet-async";
import { useQuery } from "@tanstack/react-query";
import {
  GetClientUserListing,
  GetPrices,
  updateClientStatus,
} from "../../../../Api/clientUser";
import {
  Confirmation,
  ErrorToast,
  SuccessToast,
} from "../../../../Utils/SweetAlert";
import "../../style.css";
import Pagination from "../../../../Utils";
import { UserSearchBarInitialValues } from "../../constant";
import SearchBox from "../../../../Components/SearchBox";
import Tooltip from "@mui/material/Tooltip";
import { useNavigate } from "react-router-dom";
import { IconButton } from "@mui/material";
import { IoEye } from "react-icons/io5";
import NoImage from "../../../../assets/Images/no-image.png";
import SubscriptionForm from "../SubscriptionForm";

const ClientUserListing = () => {
  const formRef = useRef();
  const [page, setPage] = useState(0);
  const [limit, setLimit] = useState(10);
  const navigate = useNavigate();

  const [openModal, setOpenModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [monthlyPrice, setMonthlyPrice] = useState(null);
  const [yearlyPrice, setYearlyPrice] = useState(null);

  const handleGiveSubscription = (user) => {
    setSelectedUser(user);
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setSelectedUser(null);
    setOpenModal(false);
  };


  const {
    isFetching: userDataLoading,
    data: userData,
    refetch,
  } = useQuery({
    queryKey: ["get-client-user-list", limit, page],
    queryFn: () =>
      GetClientUserListing({
        limit: limit,
        page: page === 0 ? 1 : page + 1,
        search: formRef?.current?.values?.search,
      }),
    throwOnError: (error) => {
      ErrorToast(error?.response?.data?.message || "Server Error.");
    },
  });

  const getPrice = async () => {
    try {
      const res = await GetPrices();
      if (res.status === 1) {
        setMonthlyPrice(res.data.monthly?.toString()?.includes(".00") ? res.data.monthly : `${res.data.monthly}.00`);
        setYearlyPrice(res.data.yearly.toString()?.includes(".00") ? res.data.yearly : `${res.data.yearly}.00`);
      }
    } catch (error) {
      console.error("Error:", error);
    }
  }

  const handlePageChange = async (e, pageNew) => {
    setPage(pageNew);
    refetch();
  };

  const handleLimitChange = async (event) => {
    setPage(0);
    setLimit(parseInt(event.target.value, 10));
    refetch();
  };

  const handleConfirmRequest = (values) => {
    Confirmation(
      values?.status === true
        ? "Are you sure you want to active user status ?"
        : "Are you sure you want to inactive user status ?"
    ).then(async (deleteConfirm) => {
      if (deleteConfirm.isConfirmed) {
        await updateClientStatus(values)
          .then((res) => {
            SuccessToast(res?.data?.message || "Status Updated");
            refetch();
          })
          .catch((err) => {
            if (typeof err?.response?.data?.message !== "undefined") {
              ErrorToast(err?.response?.data?.message || "Server Error!!");
            } else {
              ErrorToast(err?.message || "Server Error!!");
            }
          });
      }
    });
  };

  useEffect(()=>{
    getPrice();
  },[])

  return (
    <>
      <Helmet>
        <title>OVE | Client User</title>
      </Helmet>
      <div className="page-wrapper admin-user">
        <div className="content container-fluid">
          <div className="client-wrapper">
            <div className="page-header">
              <div className="col">
                <h1 className="page-title">Users</h1>
              </div>
              <div className="col-12 col-md-3 col-lg-2 px-2">
                <Formik
                  initialValues={UserSearchBarInitialValues}
                  innerRef={formRef}
                >
                  {() => {
                    return (
                      <form>
                        <SearchBox refetch={refetch} setPage={setPage} />
                      </form>
                    );
                  }}
                </Formik>
              </div>
            </div>
            <div className="col-sm-12 table-width">
              <div className="cards-table card-table">
                <div className="card-body">
                  <div className="table-responsive custom-pagination table-no-padding">
                    <table className="table datatable table-manager mb-0" style={{ width: '100%' }}>
                      <thead className="thead-light">
                        <tr>
                          <th></th>
                          <th className="col-name text-start" style={{ width: '20%' }}>Name</th>
                          <th className="col-email text-start" style={{ width: '25%' }}>Email</th>
                          <th className="col-age text-start" style={{ width: '10%' }}>Age</th>
                          <th className="col-age text-start" style={{ width: '15%' }}>Plan</th>
                          <th className="col-status text-start" style={{ width: '15%' }}>Status</th>
                          <th className="col-action text-center" style={{ width: '15%' }}>Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {userDataLoading && (
                          <tr>
                            <td align="center" colSpan={7}>
                              <div className="d-flex justify-content-center align-items-center py-4">
                                <div className="spinner-border loader-theme" role="status" />
                              </div>
                            </td>
                          </tr>
                        )}
                        {!userDataLoading && userData !== undefined && (
                          <>
                            {userData?.data?.data.map((item, index) => {
                              return (
                                <tr key={index} className="table">
                                  <td>
                                    <img
                                      src={item?.profilePicUrl || NoImage}
                                      alt={item?.name || "User"}
                                      className="rounded-circle me-2"
                                      style={{ width: "32px", height: "32px", objectFit: "cover" }}
                                    />
                                  </td>
                                  <td>
                                    <div className="d-flex align-items-center">
                                      <Tooltip title={item.name} placement="top">
                                        <span>
                                          {item.name?.length >= 45
                                            ? item.name.substring(0, 40) + "..."
                                            : item.name || "-"}
                                        </span>
                                      </Tooltip>
                                    </div>
                                  </td>
                                  <td>
                                    <Tooltip title={item.email} placement="top">
                                      <span>
                                        {item.email?.length >= 45
                                          ? item.email.substring(0, 40) + "..."
                                          : item.email || "-"}
                                      </span>
                                    </Tooltip>
                                  </td>
                                  <td>
                                    {item?.age ? `${item?.age} Years` : "-"}
                                  </td>
                                  <td className="text-capitalize">
                                    {item?.subscriptionPlanType ? item?.subscriptionPlanType : (
                                      <button
                                        onClick={() => handleGiveSubscription(item)}
                                        className="primary-button action-btn"
                                      >
                                        Give Subscription
                                      </button>
                                    )}
                                  </td>
                                  <td>
                                    {item?.status == true ? (
                                      <button
                                        onClick={() =>
                                          handleConfirmRequest({
                                            id: item?._id,
                                            status:
                                              item?.status === true
                                                ? false
                                                : true,
                                          })
                                        }
                                        class="active-btn btn-success"
                                        disabled={
                                          item?.role === "superadmin"
                                            ? true
                                            : false
                                        }
                                      >
                                        Active
                                      </button>
                                    ) : (
                                      (
                                        <button
                                          onClick={() =>
                                            handleConfirmRequest({
                                              id: item?._id,
                                              status:
                                                item?.status === true
                                                  ? false
                                                  : true,
                                            })
                                          }
                                          class="inactive-btn btn-danger"
                                          disabled={
                                            item?.role === "superadmin"
                                              ? true
                                              : false
                                          }
                                        >
                                          Inactive
                                        </button>
                                      ) || "-"
                                    )}
                                  </td>
                                  <td className="category-td-padding-remove col-action text-center">
                                    <Tooltip
                                      title="View"
                                      className="btn btn-sm btn-white text-success insight-stickey-actions-btn"
                                      onClick={() =>
                                        navigate("/admin/client-user-detail", {
                                          state: { id: item?._id },
                                        })
                                      }
                                    >
                                      <IconButton>
                                        <IoEye
                                          className="text-secondary icon-color p-0"
                                        />
                                      </IconButton>
                                    </Tooltip>
                                  </td>
                                </tr>
                              );
                            })}
                            {userData?.data?.data?.length === 0 && (
                              <tr>
                                <td className="text-center" colSpan={7}>
                                  No Records Found.
                                </td>
                              </tr>
                            )}
                          </>
                        )}
                        {selectedUser && (
                          <SubscriptionForm user={selectedUser} onClose={handleCloseModal} openModal={openModal} refetch={refetch} yearlyPrice={yearlyPrice} monthlyPrice={monthlyPrice} />
                        )}

                        {/* {open === true && (
                          <ViewUserDetails
                            onClose={handleDetailModal}
                            details={detail}
                          />
                        )} */}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
            <Pagination
              totalItem={userData?.data?.totalRecords || 0}
              itemsPerPage={limit}
              page={
                !userData?.data?.totalRecords ||
                  userData?.data?.totalRecords <= 0
                  ? 0
                  : page
              }
              handleChangePage={handlePageChange}
              handleChangeRowsPerPage={handleLimitChange}
              colSpan={7}
            />
          </div>
        </div>
      </div>
    </>
  );
};

export default ClientUserListing;
